import { MainButton } from 'elements/button/MainButton';
import { GStory_cards_LinkCard_links } from 'graphql/fragment/__generated__/GStory';
import { DetailQuery_moment_cards_LinkCard } from 'graphql/query/__generated__/DetailQuery';
import { useAnchorScroll } from 'hooks/general/useAnchorScroll';
import { useZoomToMarker } from 'hooks/map/useZoomToMarker';
import { useAnalytics } from 'libs/utils/site/analytics';
import { normalizeLink } from 'libs/utils/text/normalizeLink';
import { EVPcScrollAnchors } from 'pages/EventViewPage/EventViewPagePC/eVScrollAnchors';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { CtxEventView } from 'session/ctxEventView';
import styled from 'styled-components/macro';

interface Props {
    card: DetailQuery_moment_cards_LinkCard;
    className?: string;
}

export const LinkCard = (props: Props): JSX.Element => {
    const ctxGuide = useContext(CtxEventView);
    const analytics = useAnalytics();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { zoomToMarker } = useZoomToMarker();
    const { scrollToAnchor } = useAnchorScroll();

    const goToMarkerPrefix = 'marker://';

    function handleLocationClick(link: GStory_cards_LinkCard_links) {
        const markerId = link.url.replace(goToMarkerPrefix, '');
        analytics.track('detail_card_link.link_clicked', {
            cardId: props.card.cardId,
            linkTitle: link.title,
            linkUrl: link.url
        });

        if (!ctxGuide) {
            zoomToMarker(markerId);
            return;
        }

        const eventData = ctxGuide.eventData;
        const mobileUrl = `/event/${eventData.eventId}/map?markerId=${markerId}`;

        if (!ctxGuide.isWide) {
            navigate(mobileUrl);
        } else {
            searchParams.set('markerId', markerId);
            searchParams.set('zoomTo', '1');
            setSearchParams(searchParams);
            scrollToAnchor(EVPcScrollAnchors.Map);
        }
    }

    return (
        <S_LinkCard className={props.className}>
            {props.card.links.map((link) => {
                if (!link.url || !link.title) return null;

                if (link.url.startsWith(goToMarkerPrefix)) {
                    return (
                        <S_MainButtonMarker
                            key={link.url}
                            type="event-secondary"
                            iconSet="far"
                            iconName="location-dot"
                            onClick={() => handleLocationClick(link)}
                        >
                            {link.title}
                        </S_MainButtonMarker>
                    );
                }

                return (
                    <S_Link
                        key={link.url}
                        onClick={() => {
                            analytics.track('detail_card_link.link_clicked', {
                                cardId: props.card.cardId,
                                linkTitle: link.title,
                                linkUrl: link.url
                            });
                        }}
                        href={normalizeLink(link.url)}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <S_MainButtonLink
                            type="event"
                            iconSet="far"
                            iconName="arrow-up-right-from-square"
                        >
                            {link.title}
                        </S_MainButtonLink>
                    </S_Link>
                );
            })}
        </S_LinkCard>
    );
};

const S_LinkCard = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin: 0 0 -10px 0;
    justify-content: center;
`;

const S_Link = styled.a`
    margin: 0 5px 10px 5px;
`;

const S_MainButtonLink = styled(MainButton)``;
const S_MainButtonMarker = styled(MainButton)`
    margin: 0 5px 10px 5px;
`;
